import { Link } from "react-router-dom";
import { Instagram, Linkedin, Mail, Phone, MapPin } from "lucide-react";
import { CLINIC } from "@/constants/clinic";
import { FOOTER } from "@/constants/testIds/clinic";

export default function Footer() {
    return (
        <footer
            data-testid={FOOTER.root}
            className="relative mt-24 text-[hsl(var(--bg-chalk))] overflow-hidden"
            style={{
                background:
                    "radial-gradient(60% 80% at 80% 0%, hsl(var(--brand-gold) / 0.35) 0%, transparent 55%), radial-gradient(60% 70% at 10% 100%, hsl(var(--brand-gold) / 0.18) 0%, transparent 60%), hsl(var(--brand-ink-deep))",
            }}
        >
            <div className="ihx-container py-16 lg:py-20 relative">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-14">
                    <div className="md:col-span-5">
                        <span className="ihx-eyebrow" style={{ color: "hsl(var(--brand-gold-soft))" }}>
                            Evidence-based · Personalised
                        </span>
                        <h3 className="font-serif text-[hsl(var(--bg-chalk))] text-[34px] leading-[1.05] mt-5 max-w-md tracking-[-0.02em]">
                            Care that <span className="ihx-italic" style={{ color: "hsl(var(--brand-gold))" }}>transforms.</span>
                            <br />Medicine that doesn’t just treat.
                        </h3>
                        <p className="mt-6 text-[15px] leading-[1.7] text-[hsl(var(--bg-chalk))]/70 max-w-md">
                            A personalised, hybrid practice led by{" "}
                            <abbr title="Doctor">Dr.</abbr> Ishani Hanspal (
                            <abbr title="Bachelor of Medicine, Bachelor of Surgery">MBBS</abbr>,{" "}
                            <abbr title="Doctor of Medicine">MD</abbr>,{" "}
                            <abbr title="International Board of Lifestyle Medicine">IBLM</abbr>
                            ).
                        </p>
                        <Link
                            to="/apply"
                            data-testid={FOOTER.applyCta}
                            className="ihx-btn-primary mt-8"
                            style={{
                                backgroundColor: "hsl(var(--brand-gold))",
                                borderColor: "hsl(var(--brand-gold))",
                            }}
                        >
                            Apply for Consultation
                            <span className="ihx-arrow" aria-hidden>→</span>
                        </Link>
                    </div>

                    <div className="md:col-span-3">
                        <p className="text-[10.5px] tracking-[0.28em] uppercase text-[hsl(var(--bg-chalk))]/45 mb-5">
                            Explore
                        </p>
                        <ul className="space-y-3 text-[15px] text-[hsl(var(--bg-chalk))]/80">
                            <li><Link to="/about" className="hover:text-[hsl(var(--bg-chalk))] transition-colors">About Dr. Hanspal</Link></li>
                            <li><Link to="/services" className="hover:text-[hsl(var(--bg-chalk))] transition-colors">Services</Link></li>
                            <li><Link to="/approach" className="hover:text-[hsl(var(--bg-chalk))] transition-colors">The Approach</Link></li>
                            <li><Link to="/faq" className="hover:text-[hsl(var(--bg-chalk))] transition-colors">FAQ</Link></li>
                            <li><Link to="/contact" className="hover:text-[hsl(var(--bg-chalk))] transition-colors">Contact</Link></li>
                        </ul>
                    </div>

                    <div className="md:col-span-4">
                        <p className="text-[10.5px] tracking-[0.28em] uppercase text-[hsl(var(--bg-chalk))]/45 mb-5">
                            Reach the Clinic
                        </p>
                        <ul className="space-y-4 text-[15px] text-[hsl(var(--bg-chalk))]/85">
                            <li className="flex items-start gap-3">
                                <Mail size={16} className="mt-1" style={{ color: "hsl(var(--brand-gold-soft))" }} strokeWidth={1.6} />
                                <a href={`mailto:${CLINIC.email}`} className="hover:text-[hsl(var(--bg-chalk))] transition-colors break-all">
                                    {CLINIC.email}
                                </a>
                            </li>
                            <li className="flex items-start gap-3">
                                <Phone size={16} className="mt-1" style={{ color: "hsl(var(--brand-gold-soft))" }} strokeWidth={1.6} />
                                <a href={`tel:${CLINIC.phone.replace(/\s+/g, "")}`} className="hover:text-[hsl(var(--bg-chalk))] transition-colors">
                                    {CLINIC.phone}
                                </a>
                            </li>
                            <li className="flex items-start gap-3">
                                <MapPin size={16} className="mt-1" style={{ color: "hsl(var(--brand-gold-soft))" }} strokeWidth={1.6} />
                                <span>{CLINIC.cities.join("  ·  ")}  ·  Virtual</span>
                            </li>
                        </ul>

                        <div className="flex items-center gap-3 mt-7">
                            <a
                                href={CLINIC.social.instagram}
                                target="_blank"
                                rel="noreferrer noopener"
                                aria-label="Instagram"
                                className="w-10 h-10 grid place-items-center rounded-full border border-[hsl(var(--bg-chalk))]/20 hover:border-[hsl(var(--brand-gold))] hover:text-[hsl(var(--bg-chalk))] text-[hsl(var(--bg-chalk))]/75 transition-colors"
                            >
                                <Instagram size={16} strokeWidth={1.6} />
                            </a>
                            <a
                                href={CLINIC.social.linkedin}
                                target="_blank"
                                rel="noreferrer noopener"
                                aria-label="LinkedIn"
                                className="w-10 h-10 grid place-items-center rounded-full border border-[hsl(var(--bg-chalk))]/20 hover:border-[hsl(var(--brand-gold))] hover:text-[hsl(var(--bg-chalk))] text-[hsl(var(--bg-chalk))]/75 transition-colors"
                            >
                                <Linkedin size={16} strokeWidth={1.6} />
                            </a>
                        </div>
                    </div>
                </div>

                <div className="mt-14 pt-8 border-t border-[hsl(var(--bg-chalk))]/12 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-[12.5px] text-[hsl(var(--bg-chalk))]/50">
                    <span>© {new Date().getFullYear()} Dr. Ishani Hanspal Clinic. All rights reserved.</span>
                    <span className="max-w-xl md:text-right">
                        Editorial content on this site is for educational purposes and is not a substitute for individualised medical advice.
                    </span>
                </div>
            </div>
        </footer>
    );
}
